import React, { useCallback, useEffect } from "react";

import PropTypes from 'prop-types';
import { variableCurrencyValue } from "../../../components/field-table";
import { Button, TextField } from "@mui/material";
import { FieldTable } from "../../../components/FieldTable/FieldTable";
import { useOutletContext } from "react-router-dom";
import { CampaignOutletContextValue } from "../form";
import { useUserContext } from "../../../contexts/user-context";
import { FieldTableColumnProps } from "../../../components/FieldTable/FieldTableTypes";
import { registerCreativeStudioBeforeSave } from "../Editor";

// a brand-new section (top-level). No id yet (created on save); direct mode, zero amounts.
const newSection = (): any => ({
    name: '',
    notes: '',
    mode: 'direct',
    children: []
});

// a brand-new subcomponent (a child of a section). detail-only: no campaignComponent row.
const newSubcomponent = (): any => ({
    name: '',
    notes: '',
    campaignComponent: {
        unit_value_local: 0,
        unit_value_estimated_local: 0,
        unit_value_actual: 0
    }
});

const num = (v: any): number => {
    const n = parseInt(v);
    return isNaN(n) ? 0 : n;
};

const entryAmounts = (entry: any): { quote: number, estimated: number, actual: number } => {
    const cc = entry?.campaignComponent;
    if (cc) {
        return { quote: num(cc.unit_value_local), estimated: num(cc.unit_value_estimated_local), actual: num(cc.unit_value_actual) };
    }
    return { quote: num(entry?.unit_value_local), estimated: num(entry?.unit_value_estimated_local), actual: num(entry?.unit_value_actual) };
};

// the section's displayed amounts: subcomponents mode = sum of children; direct mode = own values
const sectionAmounts = (section: any): { quote: number, estimated: number, actual: number } => {
    if (section?.mode === 'subcomponents') {
        const children: any[] = section.children ?? [];
        return children.reduce((acc: { quote: number, estimated: number, actual: number }, c: any) => {
            const a = entryAmounts(c);
            acc.quote += a.quote; acc.estimated += a.estimated; acc.actual += a.actual;
            return acc;
        }, { quote: 0, estimated: 0, actual: 0 });
    }
    return entryAmounts(section);
};

// a permission-gated amount column (returns null when the user lacks the permission)
const amountColumn = (
    accessor: 'quote' | 'estimated' | 'actual',
    header: string,
    permissionCode: string | null,
    hasPermission: (code: string) => boolean,
    isReadOnly: (hasPriceImpact: boolean) => boolean
): FieldTableColumnProps<any> | null => {
    if (permissionCode && !hasPermission(permissionCode)) {
        return null; // hidden: the user lacks the permission
    }
    return {
        header,
        accessor,
        dataType: 'currency',
        readOnly: () => isReadOnly(true),
        options: {
            currency: { accessor: 'currency', allowContextCurrency: true }
        },
        // eslint-disable-next-line react/prop-types, react/display-name
        summaries: {
            footer: ({ rows, currency }: any) => {
                const value = (rows ?? []).reduce((total: number, current: any) => total + sectionAmounts(current)[accessor], 0);
                return <>{<b style={{ display: 'flex', alignItems: 'center' }}>Total</b>} {variableCurrencyValue({ currency: currency, value: value })}</>;
            }
        }
    };
};

const CreativeStudioEntryPane = () => {
    const {
        values,
        setValue,
        loading,
        readOnly,
        errors,
        setTab,
        isReadOnly
    } = useOutletContext<CampaignOutletContextValue>();
    const user = useUserContext();
    const hasEstimated = user.hasPermission('campaigns.creative_studio_estimated');
    const hasActual = user.hasPermission('campaigns.creative_studio_actual');

    const allowAddDelete = !(readOnly || isReadOnly(true));

    // the rows FieldTable edits: one per section. `quote/estimated/actual` are the
    // section's *displayed* amounts (own in direct mode, sum of children in subcomponents).
    const tableRows = (values?.creativeStudioEntries ?? []).map((section: any) => ({
        id: section.id,
        creation_id: section.creation_id,
        name: section.name,
        notes: section.notes,
        mode: section.mode,
        children: section.children ?? [],
        currency: section?.campaignComponent?.currency,
        ...sectionAmounts(section)
    }));

    // write-back (D3): before save, a subcomponents-mode section's persisted amounts are
    // the sum of its children; subcomponents own no campaignComponent row.
    const applyDerivedSums = useCallback((vals: any) => {
        (vals.creativeStudioEntries ?? []).forEach((section: any) => {
            if (section.mode !== 'subcomponents') return;
            const children: any[] = section.children ?? [];
            const sum = children.reduce((acc: { quote: number, estimated: number, actual: number }, c: any) => {
                const a = entryAmounts(c);
                acc.quote += a.quote; acc.estimated += a.estimated; acc.actual += a.actual;
                return acc;
            }, { quote: 0, estimated: 0, actual: 0 });
            section.campaignComponent = {
                ...(section.campaignComponent ?? {}),
                unit_value_local: sum.quote,
                unit_value_estimated_local: sum.estimated,
                unit_value_actual: sum.actual
            };
            children.forEach((c: any) => { delete c.campaignComponent; }); // subcomponents are detail-only
        });
        return vals;
    }, []);

    // register the derived-sums transform so it runs before save
    useEffect(() => {
        registerCreativeStudioBeforeSave(applyDerivedSums);
    }, [applyDerivedSums]);

    // add a subcomponent under a section (subcomponents mode)
    const addSubcomponent = (sectionIdx: number) => {
        setValue(`creativeStudioEntries[${sectionIdx}].children`, (children: any[]) => [...(children ?? []), newSubcomponent()]);
    };
    // remove a subcomponent
    const removeSubcomponent = (sectionIdx: number, childIdx: number) => {
        setValue(`creativeStudioEntries[${sectionIdx}].children`, (children: any[]) => {
            const list = [...(children ?? [])];
            list.splice(childIdx, 1);
            return list;
        });
    };

    const columns: FieldTableColumnProps<any>[] = [
        { accessor: 'data.id', noSort: true, dataType: 'expander' },
        { header: 'Name', accessor: 'name', dataType: 'text' },
        {
            header: 'Mode',
            accessor: 'mode',
            dataType: 'text',
            // eslint-disable-next-line react/prop-types, react/display-name
            editor: ({ row, rowName, setValue: rowSet }: any) => (
                <span style={{ display: 'inline-flex', gap: 4 }}>
                    <Button size="small" variant={row?.mode !== 'subcomponents' ? 'contained' : 'outlined'}
                        disabled={!allowAddDelete}
                        onClick={() => rowSet && rowSet(`${rowName}.mode`, 'direct')}>direct</Button>
                    <Button size="small" variant={row?.mode === 'subcomponents' ? 'contained' : 'outlined'}
                        disabled={!allowAddDelete}
                        onClick={() => rowSet && rowSet(`${rowName}.mode`, 'subcomponents')}>subcomponents</Button>
                </span>
            )
        },
    ];
    const quoteCol = amountColumn('quote', 'Quote Price', null, user.hasPermission, isReadOnly);
    const estimatedCol = amountColumn('estimated', 'Predicted Cost', 'campaigns.creative_studio_estimated', user.hasPermission, isReadOnly);
    const actualCol = amountColumn('actual', 'Actual Cost', 'campaigns.creative_studio_actual', user.hasPermission, isReadOnly);
    if (quoteCol) columns.push(quoteCol);
    if (estimatedCol) columns.push(estimatedCol);
    if (actualCol) columns.push(actualCol);

    return <FieldTable
        columns={columns}
        name="creativeStudioEntries"
        newRow={() => newSection()}
        setValue={setValue}
        values={tableRows}
        newRowLabel={allowAddDelete ? "Add section" : null}
        loading={loading}
        hasExport={false}
        errors={errors}
        readOnly={readOnly}
        className="campaigns__creative-studio-entries is-scrollable"
    >{({ name, row }) => {
        // section notes + (in subcomponents mode) the subcomponent controls/rows
        const sectionIdx = name.lastIndexOf('[');
        const idx = name.slice(sectionIdx + 1, name.lastIndexOf(']'));
        const children: any[] = row?.children ?? [];
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 8 }}>
                <TextField sx={{ marginTop: 0, marginBottom: 0 }} disabled={readOnly} multiline
                    name={name + '.notes'} onChange={(evt) => setValue(evt.target.name, evt.target.value)}
                    fullWidth value={row.notes} label="Notes" />
                {row?.mode === 'subcomponents' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <Button size="small" disabled={!allowAddDelete} onClick={() => addSubcomponent(idx)}>Add subcomponent</Button>
                        {children.map((child: any, cIdx: number) => (
                            <SubcomponentRow key={cIdx} row={child} sectionIdx={idx} childIdx={cIdx}
                                hasEstimated={hasEstimated} hasActual={hasActual} allowAddDelete={allowAddDelete}
                                setValue={setValue} onRemove={removeSubcomponent} />
                        ))}
                    </div>
                )}
            </div>
        );
    }}</FieldTable>;
};

// one subcomponent: name + the three amounts + notes + a delete control.
// amount fields are written to the section's children array via a function update so
// only this child's amount is mutated.
function SubcomponentRow(props: any) {
    const { row, sectionIdx, childIdx, hasEstimated, hasActual, allowAddDelete, setValue, onRemove } = props;
    const setAmount = (accessor: string) => (evt: React.ChangeEvent<HTMLInputElement>) => {
        setValue(`creativeStudioEntries[${sectionIdx}].children`, (children: any[]) => {
            const list = [...(children ?? [])];
            const c = list[childIdx];
            if (!c) return list;
            const field = accessor === 'quote' ? 'unit_value_local' : accessor === 'estimated' ? 'unit_value_estimated_local' : 'unit_value_actual';
            c.campaignComponent = { ...(c.campaignComponent ?? {}), [field]: num(evt.target.value) };
            return list;
        });
    };
    const a = entryAmounts(row);
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr auto', gap: 6, paddingLeft: 12 }}>
            <TextField size="small" value={row.name} label="Subcomponent" disabled={!allowAddDelete}
                onChange={(e) => setValue(`creativeStudioEntries[${sectionIdx}].children`, (children: any[]) => {
                    const list = [...(children ?? [])]; list[childIdx] = { ...list[childIdx], name: e.target.value }; return list;
                })} />
            {hasEstimated && <TextField size="small" value={a.estimated} label="Predicted" type="number" disabled={!allowAddDelete} onChange={setAmount('estimated')} />}
            {hasActual && <TextField size="small" value={a.actual} label="Actual" type="number" disabled={!allowAddDelete} onChange={setAmount('actual')} />}
            <TextField size="small" value={row.notes} label="Notes" disabled={!allowAddDelete}
                onChange={(e) => setValue(`creativeStudioEntries[${sectionIdx}].children`, (children: any[]) => {
                    const list = [...(children ?? [])]; list[childIdx] = { ...list[childIdx], notes: e.target.value }; return list;
                })} />
            <Button size="small" disabled={!allowAddDelete} onClick={() => onRemove(sectionIdx, childIdx)}>✕</Button>
        </div>
    );
}

CreativeStudioEntryPane.propTypes = {
    values: PropTypes.array,
    setValue: PropTypes.func
};

export default CreativeStudioEntryPane;
